/**
 * Login Component — NARA Care
 * Layout split: branding a la izquierda, formulario a la derecha.
 * Soporta: Email/Password, Google Sign-In, Recuperar contraseña, Registro de empresa.
 */

import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Divider,
  IconButton,
  CircularProgress,
  Alert,
  Stack,
  InputAdornment,
  Collapse,
} from "@mui/material";
import {
  Google as GoogleIcon,
  Visibility,
  VisibilityOff,
  Email as EmailIcon,
  Lock as LockIcon,
  Business as BusinessIcon,
  CheckCircle as CheckCircleIcon,
  WhatsApp as WhatsAppIcon,
  AutoAwesome as AutoAwesomeIcon,
  Visibility as VisibilityIcon,
  Cloud as CloudIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { auth } from "../firebase";
import {
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  GoogleAuthProvider,
  sendPasswordResetEmail,
  onAuthStateChanged,
} from "firebase/auth";
import { StyledDialog, inputStyles } from './common';
import { LoginWaves } from './common/MedicalIllustrations';

const API_URL = "/api";

// Partners — para footer de la columna izquierda
const PARTNERS = [
  { name: "OpenAI", icon: AutoAwesomeIcon, color: "#10a37f" },
  { name: "Anthropic", icon: VisibilityIcon, color: "#d4a27f" },
  { name: "WhatsApp", icon: WhatsAppIcon, color: "#25D366" },
  { name: "Twilio", icon: CloudIcon, color: "#f22f46" },
];

// Países y ciudades
const countryOptions = [
  { code: "CO", name: "Colombia" },
  { code: "MX", name: "México" },
  { code: "PA", name: "Panamá" },
];

const citiesByCountry = {
  CO: ["Armenia", "Barranquilla", "Bogotá", "Bucaramanga", "Cali", "Cartagena", "Medellín", "Pereira"],
  MX: ["Ciudad de México", "Guadalajara", "Monterrey", "Puebla", "Tijuana", "León", "Mérida", "Querétaro"],
  PA: ["Ciudad de Panamá", "Colón", "David", "Santiago", "Chitré", "La Chorrera"],
};

// Features destacadas en el branding
const FEATURES = [
  { icon: "🩺", text: "Expediente médico electrónico (NOM-024)" },
  { icon: "💬", text: "Chatbot médico 24/7 por WhatsApp" },
  { icon: "📅", text: "Agenda inteligente con reagendamiento conversacional" },
];

export default function Login() {
  const navigate = useNavigate();

  // Estados del formulario
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  // Estados de carga y errores
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // Estados para registro de empresa
  const [showCompanyDialog, setShowCompanyDialog] = useState(false);
  const [pendingFirebaseUser, setPendingFirebaseUser] = useState(null);
  const [companyData, setCompanyData] = useState({
    company: "",
    address: "",
    city: "",
    country: "",
    tax_id: "",
  });

  // Estado para recuperar contraseña
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotSuccess, setForgotSuccess] = useState(false);

  // Verificar si ya está autenticado
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user && localStorage.getItem("user_id")) {
        navigate("/dashboard");
      }
    });
    return () => unsubscribe();
  }, [navigate]);

  // Si el usuario regresa de un signInWithRedirect (fallback de popup),
  // procesar el resultado y autenticar contra el backend.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const result = await getRedirectResult(auth);
        if (result?.user && !localStorage.getItem("user_id")) {
          if (cancelled) return;
          setLoading(true);
          await authenticateWithBackend(result.user);
        }
      } catch (err) {
        if (cancelled) return;
        console.error("getRedirectResult error:", err);
        setError(err.message || "Error completando login con Google");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Limpiar errores después de 5 segundos
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(""), 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  /**
   * Autenticar con el backend usando token de Firebase
   */
  const authenticateWithBackend = async (firebaseUser, companyInfo = null) => {
    try {
      const idToken = await firebaseUser.getIdToken();

      const payload = { token: idToken };
      if (companyInfo) {
        payload.company = companyInfo;
      }

      const response = await fetch(`${API_URL}/auth/firebase`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (response.ok) {
        localStorage.setItem("user_id", data.user_id);
        localStorage.setItem("user_email", data.email);
        localStorage.setItem("user_name", data.name);
        navigate("/dashboard");
        return true;
      } else if (response.status === 422) {
        // Usuario nuevo, necesita datos de empresa
        setPendingFirebaseUser(firebaseUser);
        setShowCompanyDialog(true);
        return false;
      } else {
        throw new Error(data.detail || "Error de autenticación");
      }
    } catch (err) {
      throw err;
    }
  };

  /**
   * Login con Email y Password usando Firebase
   */
  const handleEmailLogin = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      setError("Por favor completa todos los campos");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      await authenticateWithBackend(userCredential.user);
    } catch (firebaseError) {
      console.error("Firebase error:", firebaseError.code);

      if (firebaseError.code === "auth/user-not-found" ||
          firebaseError.code === "auth/invalid-credential") {
        try {
          const response = await fetch(`${API_URL}/login/`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ email, password }),
          });

          if (response.ok) {
            const data = await response.json();
            localStorage.setItem("user_id", data.user_id);

            try {
              await createUserWithEmailAndPassword(auth, email, password);
              setSuccess("Cuenta migrada a Firebase exitosamente");
            } catch (createError) {
              console.log("Usuario ya existe en Firebase o error:", createError.code);
            }

            navigate("/dashboard");
          } else {
            setError("Correo o contraseña incorrectos");
          }
        } catch (legacyError) {
          setError("Correo o contraseña incorrectos");
        }
      } else if (firebaseError.code === "auth/wrong-password") {
        setError("Contraseña incorrecta");
      } else if (firebaseError.code === "auth/too-many-requests") {
        setError("Demasiados intentos. Intenta más tarde o recupera tu contraseña.");
      } else {
        setError("Error al iniciar sesión. Verifica tus credenciales.");
      }
    } finally {
      setLoading(false);
    }
  };

  /**
   * Login con Google
   */
  const handleGoogleLogin = async () => {
    setLoading(true);
    setError("");

    const provider = new GoogleAuthProvider();
    provider.setCustomParameters({ prompt: "select_account" });

    // Errores de popup que indican que el navegador bloqueó la comunicación
    // cross-origin (extensiones de privacidad, COOP estricto, modo incógnito,
    // tracking prevention, etc.). En esos casos hacemos fallback a redirect,
    // que funciona en todos los navegadores sin depender de popups.
    const POPUP_FAIL_CODES = new Set([
      "auth/popup-closed-by-user",
      "auth/popup-blocked",
      "auth/cancelled-popup-request",
      "auth/web-storage-unsupported",
      "auth/operation-not-supported-in-this-environment",
    ]);

    try {
      const result = await signInWithPopup(auth, provider);
      await authenticateWithBackend(result.user);
    } catch (err) {
      console.error("Google login (popup) error:", err);

      if (err.code && POPUP_FAIL_CODES.has(err.code)) {
        // Fallback automático: redirect login. La página se va a Google y
        // vuelve. El useEffect con getRedirectResult procesa el resultado.
        console.log("Popup falló, intentando con signInWithRedirect…");
        try {
          await signInWithRedirect(auth, provider);
          // El navegador redirige aquí — el código siguiente no se ejecuta
          return;
        } catch (redirectErr) {
          console.error("Redirect login error:", redirectErr);
          setError(redirectErr.message || "No se pudo iniciar el login con Google");
        }
      } else if (err.code === "auth/unauthorized-domain") {
        setError("Dominio no autorizado en Firebase. Avísale al equipo NARA.");
      } else if (err.code === "auth/network-request-failed") {
        setError("Sin conexión a internet o Firebase no responde.");
      } else if (err.code && err.code.startsWith("auth/")) {
        setError(`Error Firebase (${err.code}): ${err.message}`);
      } else {
        // Error del backend (sin .code)
        setError(err.message || "Error de servidor al validar el login con Google");
      }
    } finally {
      setLoading(false);
    }
  };

  /**
   * Completar registro con datos de empresa
   */
  const handleCompanySubmit = async (e) => {
    e.preventDefault();

    if (!companyData.tax_id || !companyData.company || !companyData.country || !companyData.city) {
      setError("Por favor completa todos los campos obligatorios");
      return;
    }

    setLoading(true);
    setError("");

    try {
      await authenticateWithBackend(pendingFirebaseUser, companyData);
      setShowCompanyDialog(false);
    } catch (err) {
      setError(err.message || "Error al registrar empresa");
    } finally {
      setLoading(false);
    }
  };

  /**
   * Recuperar contraseña
   */
  const handleForgotPassword = async (e) => {
    e.preventDefault();

    if (!forgotEmail) {
      setError("Ingresa tu correo electrónico");
      return;
    }

    setForgotLoading(true);
    setError("");

    try {
      await sendPasswordResetEmail(auth, forgotEmail);
      setForgotSuccess(true);
    } catch (err) {
      console.error("Reset password error:", err);
      if (err.code === "auth/user-not-found") {
        setError("No existe una cuenta con ese correo");
      } else {
        setError("Error al enviar el correo. Intenta de nuevo.");
      }
    } finally {
      setForgotLoading(false);
    }
  };

  const selectedCountryCode = countryOptions.find((c) => c.name === companyData.country)?.code;

  return (
    <Box sx={{ minHeight: "100vh", width: "100%", display: "flex", overflow: "hidden" }}>

      {/* ============== COLUMNA IZQUIERDA — Branding (solo desktop) ============== */}
      <Box sx={{
        display: { xs: "none", md: "flex" },
        width: "45%", minHeight: "100vh", flexDirection: "column",
        justifyContent: "center", alignItems: "center", position: "relative",
        background: "linear-gradient(135deg, #1e3a5f 0%, #0f172a 50%, #2d5a87 100%)",
        p: 5,
      }}>
        <LoginWaves />

        <Box sx={{ position: "relative", zIndex: 1, textAlign: "center", maxWidth: 420 }}>
          {/* Logo */}
          <Box sx={{
            width: 76, height: 76, borderRadius: "50%",
            bgcolor: "rgba(255,255,255,0.08)",
            border: "2px solid rgba(251, 191, 36, 0.25)",
            mx: "auto", mb: 3,
            display: "flex", alignItems: "center", justifyContent: "center",
            boxShadow: "0 0 40px rgba(251, 191, 36, 0.2)",
          }}>
            <img
              src="/images/logo-nara-png.png"
              alt="Nara Care"
              style={{ width: 60, height: 60, objectFit: "contain" }}
            />
          </Box>

          {/* Título */}
          <Typography sx={{
            fontFamily: '"Outfit", sans-serif',
            fontWeight: 800, fontSize: 36, color: "#fff",
            lineHeight: 1.15, mb: 1, letterSpacing: -0.5,
          }}>
            NARA Care
          </Typography>

          {/* Subtítulo */}
          <Typography sx={{
            fontSize: 16, color: "rgba(255,255,255,0.7)",
            mb: 5, lineHeight: 1.5,
          }}>
            Asistente médico inteligente por WhatsApp y EMR completo para consultorios y clínicas
          </Typography>

          {/* Línea decorativa dorada */}
          <Box sx={{
            width: 60, height: 2.5, borderRadius: 1,
            mx: "auto", mb: 4,
            background: "linear-gradient(90deg, transparent, #fbbf24, transparent)",
          }} />

          {/* Features */}
          <Box sx={{
            display: "flex", flexDirection: "column", gap: 2,
            mb: 5, textAlign: "left",
          }}>
            {FEATURES.map((item, i) => (
              <Box key={i} sx={{
                display: "flex", alignItems: "center", gap: 2,
                p: 1.5, borderRadius: 2,
                background: "rgba(255,255,255,0.04)",
                border: "1px solid rgba(255,255,255,0.06)",
                transition: "all 0.3s ease",
                "&:hover": {
                  background: "rgba(255,255,255,0.08)",
                  borderColor: "rgba(251, 191, 36, 0.2)",
                },
              }}>
                <Typography sx={{ fontSize: 24 }}>{item.icon}</Typography>
                <Typography sx={{
                  color: "rgba(255,255,255,0.85)",
                  fontSize: 13.5, fontWeight: 500,
                }}>
                  {item.text}
                </Typography>
              </Box>
            ))}
          </Box>

          {/* Powered by */}
          <Box>
            <Typography sx={{
              fontSize: 10, color: "rgba(255,255,255,0.35)",
              textTransform: "uppercase", letterSpacing: 1.5, mb: 1.5,
            }}>
              Powered by
            </Typography>
            <Box sx={{ display: "flex", gap: 2, justifyContent: "center", flexWrap: "wrap" }}>
              {PARTNERS.map((p, i) => {
                const Icon = p.icon;
                return (
                  <Box key={i} sx={{
                    display: "flex", alignItems: "center", gap: 0.6,
                    opacity: 0.55, transition: "opacity 0.3s ease",
                    "&:hover": { opacity: 0.95 },
                  }}>
                    <Box sx={{
                      width: 22, height: 22, borderRadius: "50%",
                      background: `${p.color}30`,
                      display: "flex", alignItems: "center", justifyContent: "center",
                      border: `1px solid ${p.color}50`,
                    }}>
                      <Icon sx={{ fontSize: 13, color: p.color }} />
                    </Box>
                    <Typography sx={{
                      fontSize: 11, fontWeight: 600,
                      color: "rgba(255,255,255,0.7)", letterSpacing: 0.2,
                    }}>
                      {p.name}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          </Box>
        </Box>
      </Box>

      {/* ============== COLUMNA DERECHA — Formulario ============== */}
      <Box sx={{
        flex: 1, display: "flex", alignItems: "center", justifyContent: "center",
        bgcolor: "#f8fafc", p: { xs: 2, sm: 4 },
      }}>
        <Box sx={{ width: "100%", maxWidth: 400 }}>

          {/* Logo mobile (solo en xs/sm) */}
          <Box sx={{ display: { xs: "flex", md: "none" }, justifyContent: "center", mb: 2.5 }}>
            <Box sx={{
              width: 56, height: 56, borderRadius: "50%",
              bgcolor: "#1e3a5f",
              display: "flex", alignItems: "center", justifyContent: "center",
              boxShadow: "0 8px 24px rgba(30, 58, 95, 0.3)",
            }}>
              <img
                src="/images/logo-nara-png.png"
                alt="Nara Care"
                style={{ width: 44, height: 44, objectFit: "contain" }}
              />
            </Box>
          </Box>

          {/* Título */}
          <Typography sx={{
            fontFamily: '"Outfit", sans-serif',
            fontWeight: 700, fontSize: 26, color: "#1e3a5f",
            mb: 0.5, textAlign: { xs: "center", md: "left" },
          }}>
            Iniciar sesión
          </Typography>
          <Typography sx={{
            fontSize: 14, color: "#64748b", mb: 3,
            textAlign: { xs: "center", md: "left" },
          }}>
            Accede a tu panel médico
          </Typography>

          {/* Mensajes de error/éxito */}
          <Collapse in={!!error}>
            <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setError("")}>
              {error}
            </Alert>
          </Collapse>

          <Collapse in={!!success}>
            <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setSuccess("")}>
              {success}
            </Alert>
          </Collapse>

          {/* Formulario de login */}
          <form onSubmit={handleEmailLogin}>
            <Stack spacing={1.8}>
              <TextField
                label="Correo electrónico"
                type="email"
                fullWidth
                size="small"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={loading}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailIcon sx={{ color: "#94a3b8", fontSize: 20 }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                    background: "#fff",
                    "&:hover fieldset": { borderColor: "#1e3a5f" },
                    "&.Mui-focused fieldset": { borderColor: "#1e3a5f", borderWidth: 1.5 },
                  },
                }}
              />

              <TextField
                label="Contraseña"
                type={showPassword ? "text" : "password"}
                fullWidth
                size="small"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={loading}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockIcon sx={{ color: "#94a3b8", fontSize: 20 }} />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                        size="small"
                      >
                        {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                    background: "#fff",
                    "&:hover fieldset": { borderColor: "#1e3a5f" },
                    "&.Mui-focused fieldset": { borderColor: "#1e3a5f", borderWidth: 1.5 },
                  },
                }}
              />

              {/* Link olvidé contraseña */}
              <Box sx={{ textAlign: "right", mt: -0.5 }}>
                <Typography
                  variant="caption"
                  sx={{
                    color: "#1e3a5f",
                    fontWeight: 600,
                    cursor: "pointer",
                    "&:hover": { textDecoration: "underline" },
                  }}
                  onClick={() => {
                    setForgotEmail(email);
                    setShowForgotPassword(true);
                    setForgotSuccess(false);
                  }}
                >
                  ¿Olvidaste tu contraseña?
                </Typography>
              </Box>

              {/* Botón login */}
              <Button
                type="submit"
                variant="contained"
                fullWidth
                disabled={loading}
                sx={{
                  py: 1.3,
                  borderRadius: 2,
                  fontWeight: 700,
                  fontSize: 14,
                  textTransform: "none",
                  background: "linear-gradient(135deg, #1e3a5f 0%, #2d5a87 100%)",
                  boxShadow: "0 4px 14px rgba(30, 58, 95, 0.35)",
                  "&:hover": {
                    background: "linear-gradient(135deg, #2d5a87 0%, #1e3a5f 100%)",
                    boxShadow: "0 6px 20px rgba(30, 58, 95, 0.45)",
                  },
                }}
              >
                {loading ? <CircularProgress size={22} sx={{ color: "#fff" }} /> : "Iniciar sesión"}
              </Button>
            </Stack>
          </form>

          {/* Divider */}
          <Divider sx={{ my: 2.5 }}>
            <Typography variant="caption" sx={{ color: "#94a3b8", fontSize: 11.5 }}>
              o continúa con
            </Typography>
          </Divider>

          {/* Botón Google */}
          <Button
            variant="outlined"
            fullWidth
            startIcon={<GoogleIcon />}
            onClick={handleGoogleLogin}
            disabled={loading}
            sx={{
              py: 1.1,
              borderRadius: 2,
              fontWeight: 600,
              fontSize: 14,
              textTransform: "none",
              borderColor: "#e2e8f0",
              color: "#334155",
              background: "#fff",
              "&:hover": {
                borderColor: "#1e3a5f",
                background: "#f8fafc",
              },
            }}
          >
            Google
          </Button>

          {/* Link registro */}
          <Box sx={{ textAlign: "center", mt: 3 }}>
            <Typography variant="body2" sx={{ color: "#64748b", fontSize: 13 }}>
              ¿No tienes cuenta?{" "}
              <Typography
                component="span"
                sx={{
                  color: "#1e3a5f",
                  fontWeight: 700,
                  cursor: "pointer",
                  "&:hover": { textDecoration: "underline" },
                }}
                onClick={() => navigate("/register")}
              >
                Regístrate
              </Typography>
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Dialog: Datos de empresa */}
      <StyledDialog
        open={showCompanyDialog}
        onClose={() => !loading && setShowCompanyDialog(false)}
        title="Datos de tu empresa"
        subtitle="Completa la información para finalizar tu registro"
        icon={<BusinessIcon />}
      >
        <StyledDialog.Body>
          <Collapse in={!!error}>
            <Alert severity="error" sx={{ borderRadius: 2 }}>{error}</Alert>
          </Collapse>

          <form onSubmit={handleCompanySubmit}>
            <Stack spacing={2}>
              <TextField
                label="NIT / RFC / RUT"
                value={companyData.tax_id}
                onChange={(e) => setCompanyData({ ...companyData, tax_id: e.target.value })}
                fullWidth
                required
                size="small"
                sx={inputStyles}
              />
              <TextField
                label="Nombre de la empresa"
                value={companyData.company}
                onChange={(e) => setCompanyData({ ...companyData, company: e.target.value })}
                fullWidth
                required
                size="small"
                sx={inputStyles}
              />
              <TextField
                label="Dirección"
                value={companyData.address}
                onChange={(e) => setCompanyData({ ...companyData, address: e.target.value })}
                fullWidth
                size="small"
                sx={inputStyles}
              />
              <TextField
                select
                label="País"
                value={companyData.country}
                onChange={(e) => setCompanyData({ ...companyData, country: e.target.value, city: "" })}
                fullWidth
                required
                size="small"
                SelectProps={{ native: true }}
                sx={inputStyles}
              >
                <option value="">Selecciona un país</option>
                {countryOptions.map((c) => (
                  <option key={c.code} value={c.name}>{c.name}</option>
                ))}
              </TextField>
              <TextField
                select
                label="Ciudad"
                value={companyData.city}
                onChange={(e) => setCompanyData({ ...companyData, city: e.target.value })}
                fullWidth
                required
                size="small"
                disabled={!companyData.country}
                SelectProps={{ native: true }}
                sx={inputStyles}
              >
                <option value="">Selecciona una ciudad</option>
                {(citiesByCountry[selectedCountryCode] || []).map((city) => (
                  <option key={city} value={city}>{city}</option>
                ))}
              </TextField>
            </Stack>
          </form>
        </StyledDialog.Body>
        <StyledDialog.Actions>
          <StyledDialog.CancelButton
            onClick={() => setShowCompanyDialog(false)}
            disabled={loading}
          />
          <StyledDialog.PrimaryButton
            onClick={handleCompanySubmit}
            loading={loading}
          >
            Registrar
          </StyledDialog.PrimaryButton>
        </StyledDialog.Actions>
      </StyledDialog>

      {/* Dialog: Recuperar contraseña */}
      <StyledDialog
        open={showForgotPassword}
        onClose={() => !forgotLoading && setShowForgotPassword(false)}
        title="Recuperar contraseña"
        icon={<LockIcon />}
        variant="compact"
        maxWidth="xs"
      >
        {forgotSuccess ? (
          <StyledDialog.Body compact>
            <Box sx={{ textAlign: "center", py: 1 }}>
              <CheckCircleIcon sx={{ fontSize: 56, color: "#10b981", mb: 1.5 }} />
              <Typography sx={{ fontWeight: 600, color: "#1e293b", fontSize: 16 }}>
                ¡Correo enviado!
              </Typography>
              <Typography sx={{ color: "#64748b", fontSize: 13, mt: 0.5 }}>
                Revisa tu bandeja de entrada y sigue las instrucciones para restablecer tu contraseña.
              </Typography>
            </Box>
          </StyledDialog.Body>
        ) : (
          <StyledDialog.Body compact>
            <Typography sx={{ color: "#64748b", fontSize: 13 }}>
              Ingresa tu correo y te enviaremos un enlace para restablecer tu contraseña.
            </Typography>

            <Collapse in={!!error}>
              <Alert severity="error" sx={{ borderRadius: 2 }}>{error}</Alert>
            </Collapse>

            <TextField
              label="Correo electrónico"
              type="email"
              fullWidth
              size="small"
              value={forgotEmail}
              onChange={(e) => setForgotEmail(e.target.value)}
              disabled={forgotLoading}
              autoFocus
              sx={inputStyles}
            />
          </StyledDialog.Body>
        )}
        <StyledDialog.CompactActions>
          {forgotSuccess ? (
            <StyledDialog.PrimaryButton
              fullWidth
              onClick={() => setShowForgotPassword(false)}
            >
              Entendido
            </StyledDialog.PrimaryButton>
          ) : (
            <>
              <StyledDialog.CancelButton
                fullWidth
                onClick={() => setShowForgotPassword(false)}
                disabled={forgotLoading}
              />
              <StyledDialog.PrimaryButton
                fullWidth
                onClick={handleForgotPassword}
                disabled={!forgotEmail}
                loading={forgotLoading}
              >
                Enviar
              </StyledDialog.PrimaryButton>
            </>
          )}
        </StyledDialog.CompactActions>
      </StyledDialog>
    </Box>
  );
}
